import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { I18nProvider } from "@/i18n/context";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";
import LocaleRedirect from "./components/LocaleRedirect";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const CoachingPage = lazy(() => import("./pages/CoachingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const InterviewsPage = lazy(() => import("./pages/InterviewsPage"));
const EcosystemPage = lazy(() => import("./pages/EcosystemPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const BooksPage = lazy(() => import("./pages/BooksPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const CartDrawer = lazy(() => import("./components/CartDrawer"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

const queryClient = new QueryClient();

const AppRoutes = () => (
  <>
    <ScrollToTop />
    <AnalyticsTracker />
    <Header />
    <div className="min-h-screen">
    <Suspense fallback={<div className="min-h-screen" />}>
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<LocaleRedirect />} />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      
      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />

      {/* Legacy routes redirect to locale-prefixed */}
      <Route path="/coaching" element={<Navigate to="/es/coaching-ejecutivo" replace />} />
      <Route path="/sobre" element={<Navigate to="/es/sobre-andres" replace />} />
      <Route path="/entrevistas" element={<Navigate to="/es/entrevistas-y-medios" replace />} />
      <Route path="/charlas" element={<Navigate to="/es/sobre-andres" replace />} />
      <Route path="/ecosistema" element={<Navigate to="/es/ecosistema-greenin" replace />} />
      <Route path="/blog" element={<Navigate to="/es/blog" replace />} />
      <Route path="/libros" element={<Navigate to="/es/libros-y-publicaciones" replace />} />
      <Route path="/recursos" element={<Navigate to="/es/recursos" replace />} />
      <Route path="/contacto" element={<Navigate to="/es/contacto" replace />} />

      {/* Spanish routes */}
      <Route path="/es" element={<Index />} />
      <Route path="/es/coaching-ejecutivo" element={<CoachingPage />} />
      <Route path="/es/sobre-andres" element={<AboutPage />} />
      <Route path="/es/entrevistas-y-medios" element={<InterviewsPage />} />
      <Route path="/es/charlas-y-conferencias" element={<Navigate to="/es/sobre-andres" replace />} />
      <Route path="/es/ecosistema-greenin" element={<EcosystemPage />} />
      <Route path="/es/blog" element={<BlogPage />} />
      <Route path="/es/blog/:slug" element={<BlogPostPage />} />
      <Route path="/es/libros-y-publicaciones" element={<BooksPage />} />
      <Route path="/es/recursos" element={<BooksPage />} />
      <Route path="/es/contacto" element={<ContactPage />} />
      <Route path="/es/checkout" element={<CheckoutPage />} />
      <Route path="/es/agenda" element={<BookingPage />} />
      <Route path="/es/politica-de-privacidad" element={<LegalPage page="privacy" />} />
      <Route path="/es/terminos-de-servicio" element={<LegalPage page="terms" />} />
      <Route path="/es/politica-de-pagos" element={<LegalPage page="payments" />} />
      <Route path="/es/politica-de-cookies" element={<LegalPage page="cookies" />} />
      <Route path="/es/accesibilidad" element={<LegalPage page="accessibility" />} />

      {/* English routes */}
      <Route path="/en" element={<Index />} />
      <Route path="/en/executive-coaching" element={<CoachingPage />} />
      <Route path="/en/about" element={<AboutPage />} />
      <Route path="/en/interviews-media" element={<InterviewsPage />} />
      <Route path="/en/speaking" element={<Navigate to="/en/about" replace />} />
      <Route path="/en/greenin-ecosystem" element={<EcosystemPage />} />
      <Route path="/en/blog" element={<BlogPage />} />
      <Route path="/en/blog/:slug" element={<BlogPostPage />} />
      <Route path="/en/books-publications" element={<BooksPage />} />
      <Route path="/en/resources" element={<BooksPage />} />
      <Route path="/en/contact" element={<ContactPage />} />
      <Route path="/en/checkout" element={<CheckoutPage />} />
      <Route path="/en/schedule" element={<BookingPage />} />
      <Route path="/en/privacy-policy" element={<LegalPage page="privacy" />} />
      <Route path="/en/terms-of-service" element={<LegalPage page="terms" />} />
      <Route path="/en/payment-policy" element={<LegalPage page="payments" />} />
      <Route path="/en/cookies-policy" element={<LegalPage page="cookies" />} />
      <Route path="/en/accessibility" element={<LegalPage page="accessibility" />} />

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </div>
    <Footer />
    <Suspense fallback={null}>
      <CartDrawer />
      <WhatsAppButton />
      <CookieConsent />
    </Suspense>
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <I18nProvider>
            <CartProvider>
              <AppRoutes />
            </CartProvider>
          </I18nProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
